import { useCallback, useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { Btn, Card, Empty, fmtDate, Loading, Pager, Pill, T, useAdmin } from "@/components/admin/ui";

const STATUSES = ["all", "paid", "created", "failed", "signature_failed"];
const KINDS = ["all", "plan", "credits"];

export default function AdminPayments() {
  const { me } = useAdmin();
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (status !== "all") params.set("status", status);
      if (kind !== "all") params.set("kind", kind);
      const { data } = await api.get(`/admin/payments?${params}`);
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [status, kind, page]);

  useEffect(() => {
    load();
  }, [load]);


  return (
    <OpsShell title="Payments" subtitle="Paytm orders, captures and refunds">
      <Card
        testid="admin-payments-panel"
        title="Transactions"
        hint={d ? `${d.total} records` : ""}
        actions={
          <div className="flex gap-2">
            <select
              data-testid="admin-payments-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className={`${T.input} w-36 py-1.5`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All statuses" : s.replace("_", " ")}
                </option>
              ))}
            </select>
            <select
              data-testid="admin-payments-kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                setPage(1);
              }}
              className={`${T.input} w-32 py-1.5`}
            >
              {KINDS.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All kinds" : s}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {!d ? (
          <Loading label="Loading payments" />
        ) : d.items.length === 0 ? (
          <Empty label="No payments found" hint="Try clearing the filters" icon={CreditCard} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[1020px]">
                <thead>
                  <tr>
                    <th className={T.th}>Order</th>
                    <th className={T.th}>Customer</th>
                    <th className={T.th}>Kind</th>
                    <th className={T.th}>Amount</th>
                    <th className={T.th}>Status</th>
                    <th className={T.th}>Gateway</th>
                    <th className={T.th}>Refund</th>
                    <th className={T.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((p) => (
                    <tr key={p.id} className={T.row} data-testid={`admin-payment-row-${p.id}`}>
                      <td className={T.td}>
                        <p className={`text-xs ${T.num}`}>{p.order_id}</p>
                        {p.payment_id && <p className={`text-xs ${T.num} ${T.muted}`}>{p.payment_id}</p>}
                      </td>
                      <td className={`${T.td} text-xs ${T.muted}`}>{p.user}</td>
                      <td className={T.td}>
                        <span className="text-[13px] capitalize">{p.kind}</span>
                        {p.credits ? <span className={`text-xs ${T.muted}`}> ×{p.credits}</span> : p.plan ? <span className={`text-xs ${T.muted}`}> {p.plan}</span> : null}
                      </td>
                      <td className={`${T.td} ${T.num} font-medium`}>{p.amount}</td>
                      <td className={T.td}>
                        <Pill value={p.status} />
                      </td>
                      <td className={`${T.td} text-xs ${T.muted}`}>{p.gateway_status || "—"}</td>
                      <td className={T.td}>
                        {p.refunded ? (
                          <span className={`text-[13px] ${T.num} text-amber-600 dark:text-amber-400`}>{p.refund_amount}</span>
                        ) : (
                          <span className={T.muted}>—</span>
                        )}
                      </td>
                      <td className={`${T.td} ${T.num} ${T.muted}`}>{fmtDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="admin-payments-pager" />
          </>
        )}
      </Card>

    </OpsShell>
  );
}
